import { countriesDialling, countryData, type CountryData } from "./countries";

/** A phone as it is dialled from abroad: E.164 ("+447700900123") and the
 *  country it is dialled in, which tells apart the countries sharing a
 *  calling code (+1, +44). */
export type DialledPhone = { e164: string; country: string };

/** Digits only, with a leading + kept, and "(0)" dropped where it follows
 *  a calling code ("+44 (0)7700 900123"). */
const compact = (phone: string) =>
  phone.trim().replace(/\(0\)/g, "").replace(/[\s().\-/]/g, "");

type PhoneData = NonNullable<CountryData["phone"]>;

/** A whole national number of the country, as libphonenumber's general
 *  pattern has one (any number where it gives none). */
const whole = (phone: PhoneData, number: string) =>
  !phone.pattern || new RegExp(`^(?:${phone.pattern})$`).test(number);
const fits = (phone: PhoneData, number: string) => phone.lengths.includes(number.length);

/**
 * A national number without what it may start with that is not part of it:
 * the trunk prefix (the UK's 0), or what libphonenumber reads in its place
 * (Argentina's 0 and mobile 15, which become a 9). As libphonenumber's
 * `maybeStripNationalPrefixAndCarrierCode` and its caller: nothing is taken
 * from a whole number that would leave none, or a number of a length the
 * country does not have.
 */
function withoutPrefix(number: string, phone: PhoneData): string {
  const prefix = phone.parsing ?? phone.trunk;
  if (!prefix) return number;
  const leading = new RegExp(`^(?:${prefix})`);
  const match = leading.exec(number);
  if (!match) return number;
  const viable = whole(phone, number);
  const last = match[match.length - 1];
  const rest =
    !phone.transform || last === undefined
      ? number.slice(match[0].length)
      : number.replace(leading, phone.transform);
  if (viable && !whole(phone, rest)) return number;
  return fits(phone, rest) ? rest : number;
}

/**
 * A buyer's phone read the way the vendors' own fields read one, with
 * libphonenumber's rules (`countries.json`): with + or 00, an international
 * number, its country `country` where that shares its calling code and the
 * number is one of its, else the first of the code's countries, its main
 * one first (+1 is the United States'), whose number it is; without, a
 * national number dialled in `country`, its trunk prefix dropped (07700
 * 900123 is +447700900123; Argentina's 011 15 2345 6789 is +5491123456789),
 * or one given with the country's calling code but no + (447700900123), as
 * libphonenumber's `maybeExtractCountryCode` tells them apart: the code is
 * taken off where the number is no whole number with it and is one without
 * it, or is too long; a whole number either way is national (an Indian
 * mobile's 91, a Brazilian area code 55, a German number in Leer's 0491).
 * Null when it is no number there: no whole number of the country, by its
 * general pattern and lengths.
 */
export function readPhone(phone: string, country: string | null | undefined): DialledPhone | null {
  const digits = compact(phone);
  const international = /^(?:\+|00)(\d+)$/.exec(digits)?.[1];
  if (international) {
    for (const length of [1, 2, 3]) {
      const code = international.slice(0, length);
      const sharing = countriesDialling(code);
      if (!sharing.length) continue;
      // The country given first, where it shares the code, then the code's
      // main country and the rest: the first whose numbers it is one of (a
      // US number typed in a form set to Jamaica is the United States').
      const given = country?.toUpperCase();
      const candidates = given && sharing.includes(given) ? [given, ...sharing.filter((other) => other !== given)] : sharing;
      for (const home of candidates) {
        const data = countryData(home)?.phone;
        if (!data) continue;
        const national = withoutPrefix(international.slice(length), data);
        if (fits(data, national) && whole(data, national)) return { e164: `+${code}${national}`, country: home };
      }
      return null;
    }
    return null;
  }
  if (!/^\d+$/.test(digits) || !country) return null;
  const home = country.toUpperCase();
  const data = countryData(home)?.phone;
  if (!data) return null;
  let number = digits;
  if (digits.startsWith(data.code)) {
    const withoutCode = withoutPrefix(digits.slice(data.code.length), data);
    const tooLong = digits.length > Math.max(...data.lengths);
    if ((!whole(data, digits) && whole(data, withoutCode)) || tooLong) number = withoutCode;
  }
  const national = withoutPrefix(number, data);
  return fits(data, national) && whole(data, national) ? { e164: `+${data.code}${national}`, country: home } : null;
}

/** The phone's calling code ("44") and national number ("7700900123"). */
export function phoneParts(phone: DialledPhone): { code: string; national: string } {
  const code = countryData(phone.country)?.phone?.code ?? "";
  return { code, national: phone.e164.slice(1 + code.length) };
}

/** The number as it is dialled at home: its trunk prefix and national
 *  number ("07700900123"), what a form in that country expects. */
export function nationalPhone(phone: DialledPhone): string {
  const { national } = phoneParts(phone);
  return `${countryData(phone.country)?.phone?.trunk ?? ""}${national}`;
}

/** The number to read: its calling code apart ("+44 7700900123"). */
export function displayPhone(phone: DialledPhone): string {
  const { code, national } = phoneParts(phone);
  return `+${code} ${national}`;
}

/** The calling code a country's phones are dialled with ("+44"). */
export function diallingCode(country: string | null | undefined): string | undefined {
  const code = countryData(country)?.phone?.code;
  return code ? `+${code}` : undefined;
}
